import { useState } from 'react'

import {
  SOURCED_CAVEAT,
  SOURCE_LABEL,
  proposalSource,
  useMatterClasses,
  type MatterClass,
} from '@/api/specification'
import { Badge, Button, Card, CardHeader } from '@/components/ui'

import { SpecificationEditor } from './SpecificationEditor'

/**
 * The amendment as the attorney will file it — class, current wording,
 * proposed wording, and where the proposal came from.
 *
 * This table is the deliverable of `docs/SPECIFICATION_PLAN.md`. Rule 3 of
 * `draft_amend_v6` already describes it in words ("show the existing wording
 * and the proposed wording side by side"), and the draft renders it as prose;
 * this makes it a thing on a screen, read from the same `matter_classes` rows
 * the drafter was given and the gate checked, rather than parsed back out of a
 * document.
 *
 * ## Why the source column is the point
 *
 * Everything else here the attorney could reconstruct by reading the draft.
 * The provenance they could not: by eye, wording taken verbatim from a
 * pre-approved description and wording the model composed are the same
 * sentence. `nice_source_id` is the only thing that separates them, and it is
 * shown rather than derived — the gate's `SPEC_WORDING_UNANCHORED` is the
 * first opinion on that question and a second one here would be free to
 * disagree.
 *
 * ## What it deliberately does not say
 *
 * That an office will accept anything. The Nice Classification is
 * international; USPTO keeps its own ID Manual and EUIPO its own harmonised
 * list, and `tm_nice_class` has no office dimension to reconcile against. A
 * sourced row means the wording is real and quotable, which is a much smaller
 * claim and the only one this platform can support.
 */
export function SpecificationCard({
  matterId,
  isSpecificationMatter,
}: {
  matterId: string
  /**
   * Whether the latest run classified this matter as specification work.
   *
   * Drives only the empty state. The table itself renders whenever there are
   * rows: an attorney who recorded classes on a matter `s2` called substantive
   * still gets to see them, because the classification is a derivation and
   * theirs is a decision.
   */
  isSpecificationMatter: boolean
}) {
  const classes = useMatterClasses(matterId)
  const [editing, setEditing] = useState(false)
  const rows = classes.data ?? []

  if (classes.isPending) return null
  // An edit in flight outranks the visibility rule, and `editing` is why this
  // is not merely tidy. `isSpecificationMatter` is derived from the *selected
  // run* (`run.data?.kind === 'specification'`), and `usePipelineRun` has no
  // placeholder data — so clicking another run in the Runs list makes it
  // briefly false while that run loads. Returning null then does not unmount
  // *this* component, so `editing` stays true; it unmounts only
  // `SpecificationEditor`, taking the attorney's typed specification with it.
  // The editor reappears a moment later, open and blank, having silently
  // discarded minutes of transcription off a filing receipt.
  if (rows.length === 0 && !isSpecificationMatter && !editing) return null

  return (
    <Card role="group" aria-label="Goods and services" className="gap-3">
      <CardHeader
        title="Goods and services"
        description={
          editing
            ? 'One row per class the application claims. Saving replaces the whole specification.'
            : rows.length === 0
              ? 'Nothing on record for this application yet.'
              : 'What the application claims today, and what the response will ask for.'
        }
        actions={
          editing ? null : (
            <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
              {rows.length === 0 ? 'Record the classes' : 'Edit'}
            </Button>
          )
        }
      />

      {editing ? (
        <SpecificationEditor matterId={matterId} rows={rows} onDone={() => setEditing(false)} />
      ) : rows.length === 0 ? (
        <p className="m-0 text-body text-muted">
          This matter is specification work — the objection is answered by amending the
          identification of goods and services rather than by arguing. Recording what the
          application currently claims is what lets the drafter propose a replacement for it, and
          lets the gate check the replacement against real pre-approved wording.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-body">
            <thead>
              <tr className="border-b border-divider text-left text-meta text-muted">
                <th className="py-1 pr-3 font-normal">Class</th>
                <th className="py-1 pr-3 font-normal">Current wording</th>
                <th className="py-1 pr-3 font-normal">Proposed wording</th>
                <th className="py-1 font-normal">Source</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <ClassRow key={row.class_number} row={row} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  )
}

function ClassRow({ row }: { row: MatterClass }) {
  const source = proposalSource(row)
  return (
    <tr className="border-b border-divider align-top last:border-b-0">
      <td className="py-2 pr-3 tabular-nums">{row.class_number}</td>
      <td className="py-2 pr-3">{row.current_text}</td>
      <td className="py-2 pr-3">
        {/* An em dash, not an empty cell: a blank reads as a rendering fault, and
            "not amended" is a real answer about this class. */}
        {source === 'unamended' ? <span className="text-muted">—</span> : row.proposed_text}
      </td>
      <td className="py-2">
        {source === 'sourced' ? (
          <Badge tone="ok" title={SOURCED_CAVEAT}>
            {SOURCE_LABEL.sourced}
          </Badge>
        ) : source === 'unsourced' ? (
          <Badge tone="warn">{SOURCE_LABEL.unsourced}</Badge>
        ) : (
          <span className="text-meta text-muted">{SOURCE_LABEL.unamended}</span>
        )}
      </td>
    </tr>
  )
}
